import React, { ComponentType, lazy } from 'react';
import { clientLogger } from '../services/clientLogger';
import { safeSessionStorage } from './safeStorage';

/**
 * Lazy load a React component with async retries and error resilience.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>,
  chunkName = 'chunk'
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const sessionKey = `retry_chunk_${chunkName}`;
    const maxRetries = 2;
    let attempt = 0;

    while (attempt <= maxRetries) {
      try {
        const component = await componentImport();
        safeSessionStorage.removeItem(sessionKey);
        return component;
      } catch (error: any) {
        attempt++;
        const errorMessage = error?.message || String(error);
        console.warn(`⚠️ [lazyWithRetry] Error loading ${chunkName} (attempt ${attempt}/${maxRetries + 1}):`, errorMessage);

        if (attempt <= maxRetries) {
          // Wait 300ms, 600ms before retrying
          await new Promise((r) => setTimeout(r, attempt * 300));
        } else {
          const lowerMessage = errorMessage.toLowerCase();
          const isChunkOrNetworkError =
            lowerMessage.includes('failed to fetch') ||
            lowerMessage.includes('importing a module script failed') ||
            lowerMessage.includes('error loading dynamically imported module') ||
            lowerMessage.includes('loading chunk') ||
            lowerMessage.includes('chunkloaderror') ||
            lowerMessage.includes('dynamically imported module') ||
            lowerMessage.includes('mime type') ||
            lowerMessage.includes('failed to resolve module');

          const hasRefreshed = safeSessionStorage.getItem(sessionKey);

          // If it's a chunk/network fetch error and we haven't refreshed yet in this session
          if (isChunkOrNetworkError && !hasRefreshed && typeof window !== 'undefined') {
            safeSessionStorage.setItem(sessionKey, 'true');
            console.warn(`🔄 [lazyWithRetry] Performing automatic page refresh to recover chunk: ${chunkName}`);
            
            clientLogger.captureError({
              type: 'CHUNK_LOAD_ERROR',
              level: 'warn',
              message: `Сбой загрузки чанка ${chunkName} (попытка автоматического восстановления через reload): ${errorMessage}`,
              stack: error?.stack || '',
              metadata: {
                chunkName,
                attempt,
                autoRecoveredViaReload: true,
                url: window.location.href,
              },
            });
            clientLogger.flushImmediately();

            window.location.reload();
            return new Promise(() => {}); // Halt execution while page reloads
          }

          clientLogger.captureError({
            type: 'CHUNK_LOAD_ERROR',
            level: 'error',
            message: `Критический сбой загрузки модуля раздела ${chunkName}: ${errorMessage}`,
            stack: error?.stack || '',
            metadata: {
              chunkName,
              attempt,
              autoRecoveredViaReload: false,
              url: typeof window !== 'undefined' ? window.location.href : '',
            },
          });
          clientLogger.flushImmediately();

          throw error;
        }
      }
    }

    throw new Error(`Не удалось загрузить модуль ${chunkName}`);
  });
}



